import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Entity } from '@backstage/catalog-model';
import { identityApiRef, useApi } from '@backstage/frontend-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { usePermission } from '@backstage/plugin-permission-react';
import { HomePageStarredEntities, Visit, visitsApiRef } from '@backstage/plugin-home';
import { Cell, ToggleButton, ToggleButtonGroup } from '@backstage/ui';
import { RedHat_CVE_Record } from '@internal/backstage-plugin-devops-cve-common';
import {
  adminReadPermission,
  AccountEntity,
  getAccountSyncState,
  relTime,
  RuleEntity,
} from '@internal/backstage-plugin-catalog-devops-common';
import type { BasicPermission } from '@backstage/plugin-permission-common';
import { cveApiRef } from '../../api/CVE/CveApi';
import {
  DataColumnConfig,
  DataRow,
  DataTable,
  prettifyDateTime,
} from '@internal/backstage-plugin-devops-react';
import {
  SpmAlertInfo,
} from '@internal/backstage-plugin-devops-spm-common';
import { spmApiRef } from '@internal/backstage-plugin-devops-spm';
import styles from './home-page.module.css';

const CATEGORIES = ['Security', 'Finance', 'Housekeeping'] as const;

const greetingByHour = (hour: number): string => {
  if (hour < 5) {
    return 'Good night';
  }
  if (hour < 12) {
    return 'Good morning';
  }
  if (hour < 18) {
    return 'Good afternoon';
  }
  return 'Good evening';
};

const accountDotClass = (account: AccountEntity): string => {
  const sync = getAccountSyncState(account);
  if (sync.tone === 'success') {
    return styles.acctDotOk;
  }
  if (sync.tone === 'warning') {
    return styles.acctDotWarn;
  }
  return styles.acctDotNeutral;
};

const scoreBadgeClass = (score: number): string => {
  if (score >= 80) {
    return styles.badgeSuccess;
  }
  if (score >= 50) {
    return styles.badgeWarning;
  }
  return styles.badgeDanger;
};

const categoryBarClass = (failedRatio: number): string => {
  if (failedRatio >= 0.4) {
    return styles.barDanger;
  }
  if (failedRatio >= 0.15) {
    return styles.barWarning;
  }
  return styles.barSuccess;
};

/**
 * Widget skeleton shown while a permission is resolving, silently hidden
 * when it is denied — the homepage degrades gracefully under IAM
 * default-deny (homepage proposal §5).
 */
function Permissioned({
  permission,
  children,
}: {
  permission: BasicPermission;
  children: ReactNode;
}) {
  const { allowed, loading } = usePermission({ permission });
  if (loading) {
    return <div className={styles.permSkeleton} aria-hidden="true" />;
  }
  if (!allowed) {
    return null;
  }
  return <>{children}</>;
}

function KpiCard(props: {
  to: string;
  label: string;
  value: ReactNode;
  description: string;
}) {
  const { to, label, value, description } = props;
  return (
    <Link to={to} className={`${styles.card} ${styles.kpi}`}>
      <span className={styles.k}>{label}</span>
      <span className={styles.v}>{value}</span>
      <span className={styles.d}>{description}</span>
    </Link>
  );
}

type AlertTab = 'sync' | 'spm' | 'insights';

const HomePage = () => {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const spmApi = useApi(spmApiRef);
  const visitsApi = useApi(visitsApiRef);
  const cveApi = useApi(cveApiRef);

  const [displayName, setDisplayName] = useState('');
  const [accounts, setAccounts] = useState<AccountEntity[]>([]);
  const [domains, setDomains] = useState<Entity[]>([]);
  const [rules, setRules] = useState<RuleEntity[]>([]);
  const [activeFindings, setActiveFindings] = useState<number | undefined>(
    undefined,
  );
  const [recentAlerts, setRecentAlerts] = useState<
    (SpmAlertInfo & { account?: string })[]
  >([]);
  const [alertTab, setAlertTab] = useState<AlertTab>('sync');
  const [cveOpen, setCveOpen] = useState(true);
  const [recentVisits, setRecentVisits] = useState<Visit[]>([]);
  const [cveRows, setCveRows] = useState<any[]>([]);

  useEffect(() => {
    let cancelled = false;
    identityApi.getProfileInfo().then(info => {
      if (!cancelled) {
        setDisplayName(info.displayName || '');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [identityApi]);

  useEffect(() => {
    let cancelled = false;
    catalogApi
      .getEntities({ filter: { kind: 'Account' } })
      .then(({ items }) => {
        if (!cancelled) {
          setAccounts(items as AccountEntity[]);
        }
      })
      .catch(() => {
        // KPIs degrade to zeros.
      });
    catalogApi
      .getEntities({ filter: { kind: 'domain' } })
      .then(({ items }) => {
        if (!cancelled) {
          setDomains(items);
        }
      })
      .catch(() => {
        // Projects card degrades to empty.
      });
    catalogApi
      .getEntities({ filter: { kind: 'Rule' } })
      .then(({ items }) => {
        if (!cancelled) {
          setRules(items as RuleEntity[]);
        }
      })
      .catch(() => {
        // Compliance snapshot degrades to empty.
      });
    catalogApi
      .getEntities({ filter: { kind: 'Insight' } })
      .then(({ items }) => {
        if (cancelled) {
          return;
        }
        setActiveFindings(
          items.filter(
            insight =>
              (insight.spec as { status?: string }).status === 'Active',
          ).length,
        );
      })
      .catch(() => {
        // Insights panel hides without analyzer data.
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi]);

  useEffect(() => {
    if (accounts.length === 0) {
      return () => {};
    }
    let cancelled = false;
    Promise.all(
      accounts.map(async account =>
        spmApi
          .getRecentAlerts(account.metadata.name, 2)
          .catch(() => [] as SpmAlertInfo[]),
      ),
    ).then(batches => {
      if (cancelled) {
        return;
      }
      setRecentAlerts(
        batches
          .flatMap((batch, index) =>
            batch.map(alert => ({
              ...alert,
              account: accounts[index].metadata.title ?? accounts[index].metadata.name,
            })),
          )
          .slice(0, 5),
      );
    });
    return () => {
      cancelled = true;
    };
  }, [spmApi, accounts]);

  useEffect(() => {
    let cancelled = false;
    visitsApi
      .list({ limit: 8, orderBy: [{ field: 'timestamp', direction: 'desc' }] })
      .then(visits => {
        if (!cancelled) {
          setRecentVisits(visits.filter(visit => visit.pathname !== '/'));
        }
      })
      .catch(() => {
        // The recently card stays empty.
      });
    return () => {
      cancelled = true;
    };
  }, [visitsApi]);

  useEffect(() => {
    let cancelled = false;
    cveApi
      .getRedhatCve()
      .then(result => {
        if (cancelled || !result.succeeded) {
          return;
        }
        const rows = result.body.result as RedHat_CVE_Record[];
        if (!rows) {
          return;
        }
        setCveRows(
          rows.map((record: RedHat_CVE_Record) => ({
            CVE: record.CVE,
            severity: record.severity,
            // Red Hat descriptions repeat the package prefix ("pkg: pkg: ...").
            bugzilla_description: record.bugzilla_description.replace(
              /^([\w+.-]+):\s*\1:\s*/,
              '$1: ',
            ),
            resource_url: record.resource_url,
            public_date: prettifyDateTime(record.public_date),
          })),
        );
      })
      .catch(() => {
        // The CVE card degrades to empty.
      });
    return () => {
      cancelled = true;
    };
  }, [cveApi]);

  const accountsByDomain = useMemo(() => {
    const map = new Map<string, AccountEntity[]>();
    accounts.forEach(account => {
      const domain = account.spec.domain ?? '—';
      map.set(domain, [...(map.get(domain) ?? []), account]);
    });
    return map;
  }, [accounts]);

  const rulesByAccount = useMemo(() => {
    const map = new Map<string, RuleEntity[]>();
    rules.forEach(rule => {
      map.set(rule.spec.account, [...(map.get(rule.spec.account) ?? []), rule]);
    });
    return map;
  }, [rules]);

  const accountScores = useMemo(
    () =>
      accounts.map(account => {
        const accountRules = rulesByAccount.get(account.metadata.name) ?? [];
        const scored = accountRules.filter(
          rule => rule.spec.result !== 'Unknown',
        );
        const passed = scored.filter(rule => rule.spec.result === 'Passed')
          .length;
        return {
          account,
          score: scored.length
            ? Math.round((passed / scored.length) * 100)
            : null,
        };
      }),
    [accounts, rulesByAccount],
  );

  const domainsWithStats = useMemo(
    () =>
      domains.map(domain => {
        const domainAccounts = accountsByDomain.get(domain.metadata.name) ?? [];
        const scoreByAccount = new Map(
          accountScores.map(entry => [
            entry.account.metadata.name,
            entry.score,
          ]),
        );
        const scores = domainAccounts
          .map(account => scoreByAccount.get(account.metadata.name))
          .filter((score): score is number => score !== undefined && score !== null);
        const avg = scores.length
          ? Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length)
          : null;
        const worst = scores.length ? Math.min(...scores) : null;
        return { domain, domainAccounts, avg, worst };
      }),
    [domains, accountsByDomain, accountScores],
  );

  const byCategory = useMemo(
    () =>
      CATEGORIES.map(category => {
        const rows = rules.filter(rule => rule.spec.category === category);
        const failed = rows.filter(rule => rule.spec.result === 'Failed').length;
        return { category, failed, total: rows.length };
      }).filter(row => row.total > 0),
    [rules],
  );

  const worstAccounts = useMemo(
    () =>
      [...accountScores]
        .filter(entry => entry.score !== null)
        .sort((a, b) => (a.score ?? 0) - (b.score ?? 0))
        .slice(0, 3),
    [accountScores],
  );

  const errorAccounts = accounts.filter(
    account => getAccountSyncState(account).errorCount > 0,
  );
  const staleAccounts = accounts.filter(account => {
    const lastScan = account.spec.lastScanTime;
    if (!lastScan) {
      return false;
    }
    return Date.now() - new Date(lastScan).getTime() > 24 * 3_600_000;
  });

  const avgScore = useMemo(() => {
    const scores = accountScores
      .map(entry => entry.score)
      .filter((score): score is number => score !== null);
    return scores.length
      ? Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length)
      : 0;
  }, [accountScores]);

  const failedRules = rules.filter(rule => rule.spec.result === 'Failed');
  const scoredRules = rules.filter(rule => rule.spec.result !== 'Unknown');

  const statusLine = useMemo(() => {
    const attention: string[] = [];
    if (errorAccounts.length > 0) {
      attention.push(
        `${errorAccounts.length} account${errorAccounts.length > 1 ? 's' : ''} with sync errors`,
      );
    }
    if (staleAccounts.length > 0) {
      attention.push(
        `${staleAccounts.length} with scans older than 24h`,
      );
    }
    if (attention.length === 0) {
      return (
        <>
          All {accounts.length} accounts are healthy{' '}
          <b className={styles.statusOk}>everything looks good</b>
        </>
      );
    }
    return (
      <>
        {accounts.length} accounts · {attention.join(' · ')}{' '}
        <b className={styles.statusWarn}>needs attention</b>
      </>
    );
  }, [accounts.length, errorAccounts.length, staleAccounts.length]);

  const urlRenderCell = (row: DataRow) => {
    const link = row.resource_url as string;
    if (!link) {
      return <Cell />;
    }
    const linkSplitted = link.split('/');
    const jsonName = linkSplitted[linkSplitted.length - 1].split('.')[0];
    return (
      <Cell>
        <a href={link} target="_blank" rel="noopener noreferrer">
          {jsonName}
        </a>
      </Cell>
    );
  };

  const textRenderCell = (text: string) => <Cell>{text ?? ''}</Cell>;

  const columnsRedhat: DataColumnConfig[] = [
    {
      id: 'resource_url',
      label: 'ID',
      isRowHeader: true,
      sortValue: row => (row.resource_url as string) ?? '',
      cell: row => urlRenderCell(row),
    },
    {
      id: 'severity',
      label: 'Severity',
      sortValue: row => (row.severity as string) ?? '',
      cell: row => textRenderCell(row.severity as string),
    },
    {
      id: 'bugzilla_description',
      label: 'Description',
      cell: row => textRenderCell(row.bugzilla_description as string),
    },
    {
      id: 'public_date',
      label: 'Published at',
      sortValue: row => (row.public_date as string) ?? '',
      cell: row => textRenderCell(row.public_date as string),
    },
  ];

  return (
    <div className={styles.page}>
      {/* greeting + status line */}
      <div className={styles.hello}>
        <h1>
          {greetingByHour(new Date().getHours())}
          {displayName ? `, ${displayName}` : ''}
        </h1>
      </div>
      <div className={styles.statusline}>{statusLine}</div>

      {/* KPI band */}
      <div className={styles.kpis}>
        <KpiCard
          to="/catalog"
          label="Accounts"
          value={accounts.length}
          description={`across ${domains.length} projects${
            errorAccounts.length > 0
              ? ` · ${errorAccounts.length} need attention`
              : ''
          }`}
        />
        <KpiCard
          to="/catalog"
          label="Projects"
          value={domains.length}
          description={domains.map(d => d.metadata.title ?? d.metadata.name).slice(0, 3).join(' · ')}
        />
        <KpiCard
          to={worstAccounts[0]
            ? `/catalog/default/account/${worstAccounts[0].account.metadata.name}/compliance`
            : '/catalog'}
          label="Compliance"
          value={`${avgScore}%`}
          description="average score"
        />
        <KpiCard
          to={worstAccounts[0]
            ? `/catalog/default/account/${worstAccounts[0].account.metadata.name}/compliance?st=Failed`
            : '/catalog'}
          label="Failed checks"
          value={failedRules.length}
          description={`${failedRules.length} of ${rules.length} rules failed${
            rules.length - scoredRules.length > 0
              ? ` · ${rules.length - scoredRules.length} unknown`
              : ''
          }`}
        />
        <KpiCard
          to="/#alerts"
          label="Alerts"
          value={errorAccounts.length + staleAccounts.length}
          description="sync errors and stale scans"
        />
      </div>

      {/* widget grid */}
      <div className={styles.hgrid}>
        {/* my projects */}
        <Permissioned permission={adminReadPermission}>
          <section className={`${styles.card} ${styles.s7}`}>
            <div className={styles.wh}>
              <h2>My projects</h2>
              <span className={styles.cnt}>{domains.length}</span>
              <span className={styles.end}>
                <Link className={styles.goLink} to="/catalog">
                  Open catalog →
                </Link>
              </span>
            </div>
            <div className={styles.wb}>
              {domainsWithStats.map(({ domain, domainAccounts, avg, worst }) => (
                <Link
                  key={domain.metadata.name}
                  className={styles.proj}
                  to={`/catalog/default/domain/${domain.metadata.name}`}
                >
                  <span>
                    <span className={styles.nm}>
                      {domain.metadata.title ?? domain.metadata.name}
                    </span>
                    <br />
                    <span className={styles.mt}>
                      {domainAccounts.map(account => (
                        <span
                          key={account.metadata.name}
                          className={`${styles.acctDot} ${accountDotClass(account)}`}
                          title={account.metadata.name}
                        />
                      ))}
                      {` ${domainAccounts.length} account${
                        domainAccounts.length === 1 ? '' : 's'
                      }`}
                      {worst !== null ? ` · worst ${worst}%` : ''}
                    </span>
                  </span>
                  <span
                    className={`${styles.badge} ${styles.score} ${
                      avg === null ? styles.badgeNeutral : scoreBadgeClass(avg)
                    }`}
                  >
                    {avg === null ? '—' : `${avg}% avg`}
                  </span>
                </Link>
              ))}
              {domainsWithStats.length === 0 && (
                <div className={styles.empty}>No projects in the catalog yet.</div>
              )}
            </div>
          </section>
        </Permissioned>

        {/* compliance snapshot */}
        <section className={`${styles.card} ${styles.s5}`}>
          <div className={styles.wh}>
            <h2>Compliance snapshot</h2>
            <span className={styles.end}>
              {worstAccounts[0] && (
                <Link
                  className={styles.goLink}
                  to={`/catalog/default/account/${worstAccounts[0].account.metadata.name}/compliance`}
                >
                  Open →
                </Link>
              )}
            </span>
          </div>
          <div className={styles.wb}>
            {byCategory.map(row => {
              const ratio = row.total ? row.failed / row.total : 0;
              return (
                <div key={row.category} className={styles.crow}>
                  <span>{row.category}</span>
                  <span className={styles.bar}>
                    <div
                      className={categoryBarClass(ratio)}
                      style={{ width: `${Math.round(ratio * 100)}%` }}
                    />
                  </span>
                  <span className={styles.muted}>
                    {row.failed} / {row.total} failed
                  </span>
                </div>
              );
            })}
            {byCategory.length === 0 && (
              <div className={styles.empty}>No rules ingested yet.</div>
            )}
            <div className={styles.worst}>
              <div className={styles.worstTitle}>Needs attention</div>
              {worstAccounts.map(entry => (
                <Link
                  key={entry.account.metadata.name}
                  to={`/catalog/default/account/${entry.account.metadata.name}/compliance?cat=Security&st=Failed`}
                >
                  <span className={styles.mono}>
                    {entry.account.metadata.title ?? entry.account.metadata.name}
                  </span>
                  <span className={`${styles.badge} ${scoreBadgeClass(entry.score ?? 0)}`}>
                    {entry.score}%
                  </span>
                </Link>
              ))}
              {worstAccounts.length === 0 && (
                <div className={styles.muted}>No scored accounts yet.</div>
              )}
            </div>
          </div>
        </section>

        {/* alerts */}
        <section className={`${styles.card} ${styles.s8}`} id="alerts">
          <div className={styles.wh}>
            <h2>Alerts</h2>
            <span className={styles.cnt}>
              {errorAccounts.length + staleAccounts.length}
            </span>
            <span className={styles.end}>
              <ToggleButtonGroup
                aria-label="Alerts source"
                selectionMode="single"
                disallowEmptySelection
                selectedKeys={new Set([alertTab])}
                onSelectionChange={keys => {
                  const next = [...keys][0] as AlertTab | undefined;
                  setAlertTab(next ?? 'sync');
                }}
              >
                <ToggleButton key="sync" id="sync" size="small">
                  Sync errors
                </ToggleButton>
                <ToggleButton key="spm" id="spm" size="small">
                  SPM
                </ToggleButton>
                <ToggleButton key="insights" id="insights" size="small">
                  Insights
                </ToggleButton>
              </ToggleButtonGroup>
            </span>
          </div>
          <div className={styles.wb}>
            {alertTab === 'sync' && (
              <>
                {errorAccounts.map(account => (
                  <Link
                    key={account.metadata.name}
                    className={styles.arow}
                    to={`/catalog/default/account/${account.metadata.name}`}
                  >
                    <span className={`${styles.acctDot} ${styles.acctDotErr}`} />
                    <span className={styles.mono}>
                      {account.metadata.title ?? account.metadata.name}
                    </span>
                    <span>
                      Inventory scan failed (
                      {getAccountSyncState(account).errorCount} actions)
                    </span>
                    <span className={styles.sub}>
                      {relTime(account.spec.lastScanTime)}
                    </span>
                  </Link>
                ))}
                {staleAccounts.map(account => (
                  <Link
                    key={`stale-${account.metadata.name}`}
                    className={styles.arow}
                    to={`/catalog/default/account/${account.metadata.name}`}
                  >
                    <span className={`${styles.acctDot} ${styles.acctDotWarn}`} />
                    <span className={styles.mono}>
                      {account.metadata.title ?? account.metadata.name}
                    </span>
                    <span>Scan results older than 24h</span>
                    <span className={styles.sub}>{relTime(account.spec.lastScanTime)}</span>
                  </Link>
                ))}
                {errorAccounts.length === 0 && staleAccounts.length === 0 && (
                  <div className={styles.empty}>
                    ✓ No sync errors — every account scanned cleanly.
                  </div>
                )}
              </>
            )}
            {alertTab === 'spm' && (
              <>
                {recentAlerts.map((alert, index) => (
                  <div key={`${alert.id}-${index}`} className={styles.arow}>
                    <span className={`${styles.acctDot} ${styles.acctDotWarn}`} />
                    <span>
                      {alert.series ||
                        alert.metadata?.title ||
                        alert.ruleId ||
                        alert.resourceId}
                    </span>
                    <span className={styles.sub}>
                      {alert.account} · {relTime(alert.created_at)}
                    </span>
                  </div>
                ))}
                {recentAlerts.length === 0 && (
                  <div className={styles.empty}>No SPM alerts in this period.</div>
                )}
              </>
            )}
            {alertTab === 'insights' && (
              <div className={styles.empty}>
                {activeFindings === undefined &&
                  'No analyzer data yet — Insight entities appear after the next report.'}
                {activeFindings === 0 &&
                  '✓ No findings — external access analyzer is clean.'}
                {activeFindings !== undefined && activeFindings > 0 && (
                  <Link to="/catalog">
                    {activeFindings} active finding
                    {activeFindings > 1 ? 's' : ''} across the accounts
                  </Link>
                )}
              </div>
            )}
          </div>
        </section>

        {/* recently + starred */}
        <section className={`${styles.card} ${styles.s4}`}>
          <div className={styles.wh}>
            <h2>Recently viewed</h2>
          </div>
          <div className={styles.wb}>
            {recentVisits.slice(0, 4).map(visit => (
              <Link
                key={`${visit.pathname}-${visit.name}`}
                className={styles.arow}
                to={visit.pathname}
              >
                <span className={styles.mono}>{visit.name}</span>
                <span className={styles.sub}>
                  {relTime(new Date(visit.timestamp).toISOString())}
                </span>
              </Link>
            ))}
            {recentVisits.length === 0 && (
              <div className={styles.muted}>Nothing yet — start exploring.</div>
            )}
            <HomePageStarredEntities />
          </div>
        </section>

        {/* CVE collapsible */}
        <section className={`${styles.card} ${styles.s9} ${cveOpen ? styles.open : ''}`}>
          <div
            className={`${styles.wh} ${styles.cveHead}`}
            onClick={() => setCveOpen(current => !current)}
            role="button"
            tabIndex={0}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                setCveOpen(current => !current);
              }
            }}
          >
            <span className={styles.chev}>▸</span>
            <h2>Red Hat CVE Database — latest updates</h2>
            <span className={styles.cnt}>{cveRows.length}</span>
            <span className={`${styles.end} ${styles.muted}`}>source: cveApi · Red Hat</span>
          </div>
          {cveOpen && (
            <div className={styles.cveBody}>
              <DataTable columnConfig={columnsRedhat} data={cveRows} loading={false} />
            </div>
          )}
        </section>

        {/* quick links */}
        <section className={`${styles.card} ${styles.s3}`}>
          <div className={styles.wh}>
            <h2>Quick links</h2>
          </div>
          <div className={styles.wb}>
            <Link className={styles.qlink} to="/admin">
              Admin
            </Link>
            <Link className={styles.qlink} to="/devtools">
              DevTools
            </Link>
            <Link
              className={styles.qlink}
              to="/docs/default/component/backstage-documentation"
            >
              Docs
            </Link>
            <Permissioned permission={adminReadPermission}>
              <div className={styles.qlink}>
                IAM
                <span className={`${styles.badge} ${styles.badgeInfo}`}>soon</span>
              </div>
            </Permissioned>
          </div>
        </section>
      </div>
    </div>
  );
};

export { HomePage };
